import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Github, 
  Linkedin, 
  Mail, 
  Phone, 
  ExternalLink,
  Database,
  Code,
  BarChart3,
  Brain,
  Server,
  PieChart,
  Menu,
  X,
  ChevronDown,
  Terminal,
  Cpu,
  Activity,
  TrendingUp,
  Zap,
  Globe,
  FileCode,
  ChevronRight,
  Eye,
  Film,
  Music,
  Building,
  Layers,
  BookOpen,
  Users,
  Leaf,
  GraduationCap,
  MessageSquare,
  Smartphone,
  Table,
  Calculator,
  Lightbulb,
  FlaskConical,
  HeartPulse,
  Cloud,
  Workflow,
  Bot,
  Trophy,
  Award,
  FileCheck,
  ExternalLink as LinkIcon
} from 'lucide-react';

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.08 } }
};

// ALL 29 Projects Data - Enhanced with Skills & Value
const projects = [
  {
    id: 1,
    title: "Seattle Energy Forecast",
    subtitle: "Prédiction énergétique ML avec API de production",
    description: "Développement d'un modèle de Machine Learning pour prédire la consommation énergétique annuelle (kBtu) des bâtiments de Seattle. Pipeline MLOps complet incluant feature engineering avancé, comparaison de modèles (Random Forest, XGBoost, Ridge) et déploiement en production via API REST avec BentoML sur Azure.",
    tech: ["Python", "Scikit-Learn", "XGBoost", "BentoML", "FastAPI", "Docker", "Azure"],
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800",
    category: "Machine Learning",
    icon: Brain,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/energy-prediction-machine-learning-model",
    features: [
      "Prédiction de consommation énergétique (kBtu) avec R² > 0.85",
      "Feature engineering : âge bâtiment, surface, score ENERGY STAR",
      "Comparaison de 5 modèles ML avec validation croisée",
      "API REST déployée sur Azure avec BentoML",
      "Pipeline MLOps reproductible et scalable"
    ],
    skills: ["Machine Learning", "Feature Engineering", "MLOps", "Déploiement Cloud"],
    value: "Réduction estimée de 15% des coûts énergétiques grâce à la prédiction anticipée",
    codeSnippet: `# Pipeline ML complet avec validation croisée
from sklearn.ensemble import RandomForestRegressor
from sklearn.model_selection import cross_val_score
import xgboost as xgb

# Feature Engineering
features = ['BuildingAge', 'NumberFloors', 
            'PropertyGFATotal', 'ENERGYSTARScore']

# Entraînement avec validation croisée
model = RandomForestRegressor(n_estimators=100, random_state=42)
scores = cross_val_score(model, X_train, y_train, cv=5, scoring='r2')
print(f"R² moyen: {scores.mean():.3f} (+/- {scores.std():.3f})")

# Déploiement API avec BentoML
import bentoml
bentoml.sklearn.save_model("energy_predictor", model)`
  },
  {
    id: 2,
    title: "Analyse Marketing Bancaire",
    subtitle: "Optimisation ciblage campagne téléprospection",
    description: "Analyse exploratoire approfondie des données de campagne téléphonique bancaire (45 211 observations, 18 variables). Identification des profils clients à forte propension de souscription et recommandations métier pour optimiser le ROI des campagnes marketing.",
    tech: ["Python", "Pandas", "Seaborn", "Matplotlib", "Scipy", "Jupyter"],
    image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800",
    category: "Business Analytics",
    icon: TrendingUp,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/analyse-campagne-marketing-bancaire",
    features: [
      "Analyse de 45 211 observations clients",
      "Identification du profil type souscripteur (éducation tertiaire, 25-50 ans)",
      "Corrélation durée d'appel ↔ taux de conversion (+40%)",
      "Analyse bivariée et matrice de corrélation",
      "Recommandations métier : ciblage, scripts d'appel, réactivation"
    ],
    skills: ["Analyse exploratoire", "Statistiques descriptives", "Data Storytelling", "Business Intelligence"],
    value: "Augmentation potentielle de 25% du taux de conversion par ciblage optimisé",
    codeSnippet: `# Analyse bivariée - Taux de souscription par profil
import pandas as pd
import seaborn as sns
from scipy import stats

# Chargement et préparation
df = pd.read_csv('donnees_marketing_banque.csv')
df_majeurs = df[df['age'] >= 18]

# Taux de souscription par niveau d'éducation
subscription_rate = df.groupby('education')['y'].apply(
    lambda x: (x == 'yes').mean() * 100
).sort_values(ascending=False)

# Visualisation
fig, ax = plt.subplots(figsize=(10, 6))
sns.barplot(x=subscription_rate.index, y=subscription_rate.values)
plt.title('Taux de souscription par niveau d\\'éducation')
plt.ylabel('Taux de souscription (%)')`
  },
  {
    id: 3,
    title: "Base de Données Immobilier France",
    subtitle: "Modélisation SQL & Analyse marché territorial",
    description: "Conception d'une base de données relationnelle normalisée (3NF) du marché immobilier français. Analyse multi-niveaux avec jointures complexes pour étudier les prix au m², le dynamisme régional et les corrélations démographiques.",
    tech: ["SQL", "SQLite", "Python", "Power BI", "ERD"],
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
    category: "Data Engineering",
    icon: Building,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/base-donnees-immobilier-analyse-sql",
    features: [
      "Schéma relationnel normalisé 3NF (biens, communes, départements, régions)",
      "Jointures multi-tables complexes sur 5 niveaux",
      "Calcul prix moyen au m² par zone géographique",
      "Analyse corrélation population ↔ volume transactions",
      "Dashboard Power BI pour visualisation dynamique"
    ],
    skills: ["Modélisation BDD", "SQL avancé", "Normalisation", "Business Intelligence"],
    value: "Base de données réutilisable pour analyses immobilières nationales",
    codeSnippet: `-- Analyse prix moyen au m² par département avec volume
SELECT 
    d.nom_departement,
    r.nom_region,
    ROUND(AVG(v.prix / b.surface), 2) AS prix_m2_moyen,
    COUNT(*) AS nb_transactions,
    SUM(v.prix) AS volume_total
FROM ventes v
JOIN biens b ON v.id_bien = b.id_bien
JOIN communes c ON b.id_commune = c.id_commune
JOIN departements d ON c.id_dept = d.id_dept
JOIN regions r ON d.id_region = r.id_region
GROUP BY d.id_dept
HAVING COUNT(*) > 100
ORDER BY prix_m2_moyen DESC;`
  },
  {
    id: 4,
    title: "European Football Analytics",
    subtitle: "Analyse SQL performances 5 ligues majeures",
    description: "Analyse statistique approfondie des données de football européen (Premier League, La Liga, Serie A, Bundesliga, Ligue 1). Étude de la consistance offensive, des tendances saisonnières et de l'avantage du terrain sur plusieurs saisons.",
    tech: ["SQL", "Python", "Pandas", "Matplotlib", "SQLite"],
    image: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800",
    category: "Data Analysis",
    icon: Activity,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/european-football-sql-analysis",
    features: [
      "Analyse comparative 5 ligues européennes majeures",
      "Mesure consistance offensive par variance des buts",
      "Évolution saisonnière des performances (2008-2016)",
      "Analyse avantage Home vs Away par ligue",
      "Identification des équipes les plus régulières"
    ],
    skills: ["SQL analytique", "Statistiques sportives", "Analyse temporelle", "Data Visualization"],
    value: "Insights pour paris sportifs et analyse de performance",
    codeSnippet: `-- Analyse variance offensive par équipe (consistance)
WITH team_goals AS (
    SELECT 
        t.team_long_name,
        m.home_team_goal AS goals,
        m.season,
        l.name AS league
    FROM Match m
    JOIN Team t ON m.home_team_api_id = t.team_api_id
    JOIN League l ON m.league_id = l.id
)
SELECT 
    team_long_name,
    league,
    ROUND(AVG(goals), 2) AS avg_goals,
    ROUND(VARIANCE(goals), 3) AS goal_variance,
    COUNT(*) AS matches_played
FROM team_goals
GROUP BY team_long_name
HAVING COUNT(*) > 50
ORDER BY goal_variance ASC
LIMIT 20;`
  },
  {
    id: 5,
    title: "Spotify API Data Extraction",
    subtitle: "Pipeline ETL données musicales",
    description: "Construction d'un pipeline d'extraction de données via l'API Spotify : authentification OAuth 2.0, gestion de la pagination, parsing de JSON imbriqué et structuration en DataFrames pour analyse musicale.",
    tech: ["Python", "Requests", "Pandas", "Spotify API", "OAuth 2.0", "JSON"],
    image: "https://images.unsplash.com/photo-1614680376593-902f74cf0d41?w=800",
    category: "Data Engineering",
    icon: Music,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/spotify-api-data-extraction",
    features: [
      "Authentification OAuth 2.0 Client Credentials Flow",
      "Gestion pagination API (limit/offset) jusqu'à 10 000 items",
      "Parsing JSON imbriqué multi-niveaux",
      "Conversion durée ms → format mm:ss",
      "Export DataFrame structuré prêt pour analyse"
    ],
    skills: ["API REST", "OAuth 2.0", "ETL", "JSON Parsing", "Data Engineering"],
    value: "Pipeline réutilisable pour toute analyse de données musicales",
    codeSnippet: `# Pipeline extraction données Spotify avec pagination
import requests
import pandas as pd

def get_spotify_token(client_id, client_secret):
    auth_url = 'https://accounts.spotify.com/api/token'
    response = requests.post(auth_url, {
        'grant_type': 'client_credentials',
        'client_id': client_id,
        'client_secret': client_secret
    })
    return response.json()['access_token']

def extract_artist_tracks(artist_id, token, limit=50):
    headers = {'Authorization': f'Bearer {token}'}
    url = f'https://api.spotify.com/v1/artists/{artist_id}/top-tracks'
    response = requests.get(url, headers=headers, params={'market': 'FR'})
    
    tracks = response.json()['tracks']
    return pd.DataFrame([{
        'name': t['name'],
        'duration_min': t['duration_ms'] / 60000,
        'popularity': t['popularity']
    } for t in tracks])`
  },
  {
    id: 6,
    title: "Évolution Capacité Crânienne",
    subtitle: "Dashboard Streamlit interactif déployé",
    description: "Application Streamlit multi-pages pour explorer l'évolution de la capacité crânienne humaine à travers le temps, l'espace géographique et les caractéristiques morphologiques. Déployée sur Streamlit Cloud avec visualisations interactives et cartographie.",
    tech: ["Streamlit", "Python", "Plotly", "Folium", "Pandas", "Seaborn", "NumPy"],
    image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=800",
    category: "Data Visualization",
    icon: PieChart,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/evolution_capacite_cranienne_streamlit",
    demo: "https://endayeaime-evolution-capacite-cranienne-home-fopmqf.streamlit.app/",
    features: [
      "5 pages thématiques : Accueil, Analyse, Visualisation, Géographie, Résultats",
      "Carte interactive Folium avec géolocalisation des spécimens",
      "Visualisations Plotly dynamiques (scatter, histogrammes, boxplots)",
      "Filtres interactifs appliqués à toutes les visualisations",
      "Analyse statistique descriptive et corrélations"
    ],
    skills: ["Data Visualization", "Streamlit", "Cartographie", "Statistiques descriptives", "Déploiement Cloud"],
    value: "Application déployée permettant aux chercheurs d'explorer interactivement les données paléo-anthropologiques",
    codeSnippet: `# Dashboard Streamlit multi-pages avec cartographie
import streamlit as st
import plotly.express as px
import folium
from streamlit_folium import st_folium

st.set_page_config(page_title="Évolution Crânienne", layout="wide")

# Carte interactive des spécimens
m = folium.Map(location=[20, 0], zoom_start=2)
for _, row in df.iterrows():
    folium.CircleMarker(
        location=[row['latitude'], row['longitude']],
        radius=row['cranial_capacity'] / 200,
        popup=f"{row['species']}: {row['cranial_capacity']} cm³",
        color='blue', fill=True
    ).add_to(m)
st_folium(m, width=800)`
  },
  {
    id: 7,
    title: "Amazon Scraper Selenium",
    subtitle: "Pipeline extraction automatisé pour affiliation",
    description: "Pipeline de web scraping automatisé pour extraire les données produits Amazon (titre, prix, images, couleurs) et les préparer pour des workflows d'affiliation Pinterest. Gestion du contenu JavaScript dynamique et détection anti-bot.",
    tech: ["Python", "Selenium", "WebDriver Manager", "Pandas", "CSV", "Chrome Headless"],
    image: "https://images.unsplash.com/photo-1523474253046-8cd2748b5fd2?w=800",
    category: "Data Engineering",
    icon: Terminal,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/amazon-product-scraper",
    features: [
      "Scraping de pages JavaScript dynamiques",
      "Extraction multi-champs : titre, prix, image, couleur",
      "Détection intelligente des doublons",
      "Mode headless Chrome pour serveur",
      "Export CSV prêt pour n8n/Zapier/Make"
    ],
    skills: ["Web Scraping", "Selenium", "Automatisation", "Data Extraction", "Anti-bot handling"],
    value: "Pipeline réutilisable générant des datasets pour workflows d'affiliation automatisés",
    codeSnippet: `# Scraper Amazon avec Selenium - Anti-bot handling
from selenium import webdriver
from selenium.webdriver.common.by import By
from selenium.webdriver.support.ui import WebDriverWait
from selenium.webdriver.support import expected_conditions as EC
import pandas as pd

options = webdriver.ChromeOptions()
options.add_argument('--headless')
options.add_argument('--disable-blink-features=AutomationControlled')

driver = webdriver.Chrome(options=options)
driver.get(product_url)

# Attente explicite du contenu dynamique
wait = WebDriverWait(driver, 10)
title = wait.until(EC.presence_of_element_located(
    (By.ID, "productTitle"))).text
price = driver.find_element(By.CLASS_NAME, "a-price-whole").text

# Export CSV
df = pd.DataFrame(products)
df.to_csv('amazon_products.csv', index=False)`
  },
  {
    id: 8,
    title: "Migration MongoDB Médical",
    subtitle: "Architecture NoSQL distribuée sur AWS",
    description: "Migration complète de données médicales d'un système relationnel vers MongoDB. Architecture distribuée conteneurisée avec Docker, étude de déploiement AWS incluant réplication et stratégie de sharding pour haute disponibilité.",
    tech: ["MongoDB", "Docker", "AWS", "Python", "PyMongo", "Docker Compose"],
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
    category: "Data Engineering",
    icon: HeartPulse,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/medical-records-migration-mongodb",
    features: [
      "Modélisation document NoSQL optimisée pour données médicales",
      "Migration ETL de données relationnelles vers MongoDB",
      "Conteneurisation Docker Compose multi-services",
      "Architecture AWS avec EC2, VPC, Security Groups",
      "Réplication et sharding pour haute disponibilité"
    ],
    skills: ["NoSQL", "MongoDB", "Docker", "AWS", "Migration de données", "Architecture distribuée"],
    value: "Architecture scalable garantissant la disponibilité et la performance des données médicales critiques",
    codeSnippet: `# Migration vers MongoDB avec validation
from pymongo import MongoClient
import pandas as pd
from datetime import datetime

client = MongoClient("mongodb://localhost:27017")
db = client["medical_db"]
collection = db["patient_records"]

# Transformation et migration
df = pd.read_sql("SELECT * FROM patients", sql_conn)
records = df.to_dict('records')

# Enrichissement avec métadonnées
for record in records:
    record['migrated_at'] = datetime.utcnow()
    record['source'] = 'legacy_sql'

# Insertion avec validation
result = collection.insert_many(records)
print(f"Migrés: {len(result.inserted_ids)} documents")`
  },
  {
    id: 9,
    title: "Audit Architecture OLAP",
    subtitle: "Data Warehouse SuperSmartMarket",
    description: "Audit complet d'une architecture OLAP pour SuperSmartMarket : analyse des cubes de données, vérification des indicateurs métiers, analyse des logs pour identifier les causes d'incohérences du chiffre d'affaires et recommandations techniques.",
    tech: ["SQL", "OLAP", "Power BI", "Excel", "SQLite", "Data Warehouse"],
    image: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=800",
    category: "Data Engineering",
    icon: Layers,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/audit-architecture-olap-supermarket",
    features: [
      "Audit complet architecture OLAP Microsoft",
      "Modélisation schéma en étoile (faits/dimensions)",
      "Requêtes SQL de vérification du CA",
      "Analyse des logs pour traçabilité",
      "Rapport d'audit avec recommandations ACID"
    ],
    skills: ["Audit de données", "SQL avancé", "Architecture OLAP", "Gouvernance des données", "Documentation technique"],
    value: "Identification des causes d'incohérences CA et mise en place de bonnes pratiques d'intégrité",
    codeSnippet: `-- Audit OLAP : Vérification chiffre d'affaires
SELECT 
    c.annee, c.mois,
    p.categorie,
    e.nom_employe,
    SUM(v.montant) AS total_ventes,
    COUNT(*) AS nb_transactions,
    AVG(v.montant) AS panier_moyen
FROM fait_ventes v
JOIN dim_calendrier c ON v.id_date = c.id_date
JOIN dim_produit p ON v.id_produit = p.id_produit
JOIN dim_employe e ON v.id_employe = e.id_employe
WHERE c.annee = 2024
GROUP BY CUBE(c.annee, c.mois, p.categorie)
ORDER BY total_ventes DESC;

-- Top 10 clients par CA
SELECT client_id, SUM(montant) AS ca_total
FROM fait_ventes
GROUP BY client_id
ORDER BY ca_total DESC LIMIT 10;`
  },
  {
    id: 10,
    title: "Analyse Films TMDb",
    subtitle: "Application Streamlit cinéma",
    description: "Application web interactive pour analyser et visualiser des données de films TMDb. Dashboard avec filtres dynamiques, statistiques par genre, analyse des tendances temporelles et identification des films les mieux notés.",
    tech: ["Streamlit", "Pandas", "Plotly", "TMDb API", "Python"],
    image: "https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=800",
    category: "Data Visualization",
    icon: Film,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/movie-data-analytics-streamlit",
    features: [
      "Filtrage dynamique par année, genre et popularité",
      "Distribution des notes avec histogrammes",
      "Top 10 films les mieux notés par catégorie",
      "Évolution temporelle des sorties",
      "KPIs interactifs : budget moyen, recettes, ROI"
    ],
    skills: ["Data Visualization", "Streamlit", "Analyse exploratoire", "API REST", "Dashboard design"],
    value: "Outil d'aide à la décision pour analyse de marché cinématographique",
    codeSnippet: `# Dashboard Films TMDb avec cache
import streamlit as st
import plotly.express as px

@st.cache_data
def load_data():
    movies = pd.read_csv('tmdb_movies.csv')
    movies['release_year'] = pd.to_datetime(movies['release_date']).dt.year
    return movies

movies = load_data()

# Filtres sidebar
genre = st.sidebar.selectbox('Genre', ['Tous'] + list(movies['genre'].unique()))
year_range = st.sidebar.slider('Années', 1990, 2024, (2010, 2024))

# Filtrage
filtered = movies[(movies['release_year'].between(*year_range))]
if genre != 'Tous':
    filtered = filtered[filtered['genre'] == genre]

# Visualisation
fig = px.histogram(filtered, x='vote_average', nbins=20, 
                   title=f'Distribution des notes - {genre}')
st.plotly_chart(fig, use_container_width=True)`
  },
  {
    id: 11,
    title: "Analyse Centrales Électriques Europe",
    subtitle: "Visualisation données énergétiques",
    description: "Analyse complète des données sur les centrales électriques européennes : répartition par type d'énergie (fossile, nucléaire, renouvelable), capacités installées par pays et identification des grandes installations.",
    tech: ["Python", "Matplotlib", "Seaborn", "Bokeh", "Pandas", "NumPy"],
    image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800",
    category: "Data Visualization",
    icon: Zap,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/analyse_centrales_electriques_europe",
    features: [
      "Analyse des capacités installées par pays",
      "Répartition par type d'énergie (fossile/nucléaire/renouvelable)",
      "Identification des 20 plus grandes centrales",
      "Statistiques descriptives complètes",
      "Visualisations interactives avec Bokeh"
    ],
    skills: ["Data Visualization", "Analyse énergétique", "Statistiques descriptives", "Python", "Storytelling data"],
    value: "Vue d'ensemble du mix énergétique européen pour analyse de transition écologique",
    codeSnippet: `# Analyse centrales électriques européennes
import matplotlib.pyplot as plt
import seaborn as sns
import pandas as pd

df = pd.read_csv('power_plants_europe.csv')

# Répartition par type d'énergie
energy_capacity = df.groupby('primary_fuel')['capacity_mw'].agg(['sum', 'count', 'mean'])
energy_capacity.columns = ['Capacité_totale_MW', 'Nb_centrales', 'Capacité_moyenne']

# Top 20 plus grandes centrales
top_20 = df.nlargest(20, 'capacity_mw')[['name', 'country', 'primary_fuel', 'capacity_mw']]

# Visualisation
fig, axes = plt.subplots(1, 2, figsize=(16, 6))
sns.barplot(data=energy_capacity.reset_index(), x='primary_fuel', y='Capacité_totale_MW', ax=axes[0])
axes[0].set_title('Capacité installée par type énergie')
axes[0].tick_params(axis='x', rotation=45)`
  },
  {
    id: 12,
    title: "Hacker News Engagement",
    subtitle: "Analyse temporelle des publications",
    description: "Analyse comparative des publications Ask HN et Show HN pour déterminer lesquelles génèrent le plus d'engagement (commentaires) et identifier les créneaux horaires optimaux pour maximiser la visibilité.",
    tech: ["Python", "Pandas", "Datetime", "Jupyter", "Matplotlib"],
    image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800",
    category: "Data Analysis",
    icon: MessageSquare,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/Hacker%20News%20%E2%80%93%20Analyse%20de%20l%E2%80%99engagement%20des%20publications",
    features: [
      "Comparaison Ask HN vs Show HN (engagement)",
      "Analyse temporelle par heure et jour",
      "Identification des pics d'engagement (15h-16h EST)",
      "Statistiques descriptives des commentaires",
      "Recommandations de publication optimales"
    ],
    skills: ["Analyse temporelle", "Manipulation datetime", "Data Analysis", "Statistiques", "Insights business"],
    value: "Recommandations actionnables pour maximiser l'engagement sur Hacker News",
    codeSnippet: `# Analyse engagement Hacker News
from datetime import datetime

ask_posts = [post for post in hn_data 
             if post['title'].lower().startswith('ask hn')]

# Commentaires par heure
comments_by_hour = {}
for post in ask_posts:
    hour = datetime.strptime(
        post['created_at'], '%m/%d/%Y %H:%M').hour
    comments_by_hour[hour] = comments_by_hour.get(
        hour, 0) + post['num_comments']`
  },
  {
    id: 13,
    title: "Profitable App Profiles",
    subtitle: "Analyse App Store & Google Play",
    description: "Analyse des données d'applications mobiles pour identifier les profils d'apps rentables sur l'App Store et Google Play. Nettoyage des données et analyse des genres populaires.",
    tech: ["Python", "CSV", "Data Cleaning", "Jupyter"],
    image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800",
    category: "Data Analysis",
    icon: Smartphone,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/Guided%20Project_%20Profitable%20App%20Profiles%20for%20the%20App%20Store%20and%20Google%20Play%20Markets",
    features: [
      "Analyse 10 000+ applications",
      "Nettoyage données doublons",
      "Identification genres rentables",
      "Comparaison iOS vs Android",
      "Recommandations stratégiques"
    ],
    codeSnippet: `# Analyse applications rentables
def explore_data(dataset, start, end):
    for row in dataset[start:end]:
        print(row)
        print('\\n')

# Fréquence des genres
def freq_table(dataset, index):
    table = {}
    for row in dataset:
        genre = row[index]
        table[genre] = table.get(genre, 0) + 1
    return table

ios_genres = freq_table(ios_data, 11)`
  },
  {
    id: 14,
    title: "Analyse SQL Locations Films",
    subtitle: "Base de données cinéma type Sakila",
    description: "Exploration et analyse d'une base de données de films type Sakila avec Python et SQL. Statistiques, visualisations et requêtes avancées sur films, clients et locations.",
    tech: ["SQL", "Python", "Pandas", "Jupyter", "SQLite"],
    image: "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800",
    category: "Data Analysis",
    icon: Database,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/analyse-sql-locations-films",
    features: [
      "Requêtes SQL avancées",
      "Analyse clients et locations",
      "Statistiques par catégorie",
      "Jointures multi-tables",
      "Visualisations résultats"
    ],
    codeSnippet: `-- Top films par nombre de locations
SELECT f.title, 
       COUNT(r.rental_id) AS nb_locations,
       c.name AS categorie
FROM film f
JOIN film_category fc ON f.film_id = fc.film_id
JOIN category c ON fc.category_id = c.category_id
JOIN inventory i ON f.film_id = i.film_id
JOIN rental r ON i.inventory_id = r.inventory_id
GROUP BY f.film_id
ORDER BY nb_locations DESC
LIMIT 10;`
  },
  {
    id: 15,
    title: "Analyse Système Éducatif Mondial",
    subtitle: "Données World Bank EdStats",
    description: "Analyse des données du système éducatif mondial à partir des données World Bank EdStats. Comparaison entre pays, indicateurs de performance et tendances.",
    tech: ["Python", "Pandas", "Poetry", "Jupyter", "Data Analysis"],
    image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800",
    category: "Data Analysis",
    icon: GraduationCap,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/analyse-systeme-educatif-mondial",
    features: [
      "Analyse indicateurs éducatifs",
      "Comparaison internationale",
      "Tendances temporelles",
      "Gestion dépendances Poetry",
      "Visualisations comparatives"
    ],
    codeSnippet: `# Analyse système éducatif mondial
import pandas as pd

# Chargement données World Bank
countries = pd.read_csv('EdStatsCountry.csv')
series = pd.read_csv('EdStatsSeries.csv')

# Taux de scolarisation par région
enrollment = df.groupby('region')[
    'enrollment_rate'].mean().sort_values(ascending=False)

print(enrollment.head(10))`
  },
  {
    id: 16,
    title: "MongoDB Architecture Distribuée",
    subtitle: "NoSQL NosCités",
    description: "Conception et analyse d'une base de données NoSQL MongoDB avec architecture distribuée. Projet NosCités avec sharding, réplication et visualisation Tableau.",
    tech: ["MongoDB", "NoSQL", "Tableau", "Docker", "Python"],
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
    category: "Data Engineering",
    icon: Server,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/mongodb-distributed-data-architecture",
    features: [
      "Architecture distribuée",
      "Sharding et réplication",
      "Modélisation documents",
      "Visualisation Tableau",
      "Performance et scalabilité"
    ],
    codeSnippet: `# Configuration MongoDB Sharding
from pymongo import MongoClient

# Connexion au cluster
client = MongoClient("mongodb://localhost:27017")

# Activation sharding
admin = client.admin
admin.command('enableSharding', 'noscites_db')

# Définition shard key
admin.command('shardCollection', 
              'noscites_db.cities',
              key={'region': 'hashed'})`
  },
  {
    id: 17,
    title: "Projet SCRUM Biodiversité",
    subtitle: "Gestion de projet Data agile",
    description: "Application des principes de gestion de projet agile SCRUM dans un projet de science des données sur la biodiversité. Cadrage, sprints et livrables.",
    tech: ["Python", "Pandas", "SCRUM", "Agile", "Jupyter"],
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800",
    category: "Data Science",
    icon: Leaf,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/scrum-data-project-biodiversity",
    features: [
      "Méthodologie SCRUM",
      "Cadrage projet data",
      "Planification sprints",
      "User stories data",
      "Rétrospectives et amélioration"
    ],
    codeSnippet: `# Sprint 1 - Exploration données biodiversité
import pandas as pd

# User Story 1: Charger et explorer les données
biodiversity = pd.read_csv('species_data.csv')

# Acceptance Criteria
assert biodiversity.shape[0] > 0, "Données chargées"
assert 'species' in biodiversity.columns

# Sprint Velocity tracking
sprint_points = {'completed': 21, 'planned': 25}
velocity = sprint_points['completed'] / sprint_points['planned']`
  },
  {
    id: 18,
    title: "Streamlit Data Lab",
    subtitle: "Développement d'apps Streamlit",
    description: "Projet d'apprentissage et de développement d'applications web interactives avec Streamlit. Création d'interfaces dynamiques et visualisations en temps réel.",
    tech: ["Streamlit", "Python", "Pandas", "Plotly", "Jupyter"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
    category: "Data Visualization",
    icon: Globe,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/Streamlit_Data_Lab",
    features: [
      "Widgets interactifs",
      "Layout responsive",
      "Caching données",
      "Session state",
      "Déploiement Streamlit Cloud"
    ],
    codeSnippet: `# Application Streamlit interactive
import streamlit as st

st.set_page_config(page_title="Data Lab", 
                   layout="wide")

# Sidebar filtres
with st.sidebar:
    date_range = st.date_input("Période")
    metric = st.selectbox("Métrique", options)

# Visualisation principale
col1, col2 = st.columns(2)
with col1:
    st.metric("KPI", value, delta)
with col2:
    st.plotly_chart(fig)`
  },
  {
    id: 19,
    title: "COVID-19 Dashboard",
    subtitle: "Analyse pandémie mondiale",
    description: "Dashboard d'analyse exploratoire des données COVID-19 avec visualisations interactives des tendances mondiales, cas par pays et évolution temporelle.",
    tech: ["Python", "Pandas", "Matplotlib", "Jupyter", "EDA"],
    image: "https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?w=800",
    category: "Data Analysis",
    icon: Activity,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/covid19-streamlit-dashboard",
    features: [
      "Analyse exploratoire complète",
      "Visualisations temporelles",
      "Comparaison entre pays",
      "Taux de mortalité/guérison",
      "Tendances et prédictions"
    ],
    codeSnippet: `# Analyse COVID-19
import pandas as pd
import matplotlib.pyplot as plt

covid_data = pd.read_csv('covid_worldwide.csv')
covid_data['date'] = pd.to_datetime(covid_data['date'])

# Évolution cas par pays
top_countries = covid_data.groupby('country')[
    'confirmed'].max().nlargest(10)

plt.figure(figsize=(12, 6))
top_countries.plot(kind='bar')
plt.title('Top 10 pays - Cas confirmés')`
  },
  {
    id: 20,
    title: "Analyse Capacité Crânienne",
    subtitle: "Projet fil rouge Data Science",
    description: "Projet complet d'analyse de données sur l'évolution humaine : étude de la capacité crânienne et des traits morphologiques à travers le temps avec méthode agile.",
    tech: ["Python", "Pandas", "Seaborn", "Matplotlib", "Jupyter"],
    image: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=800",
    category: "Data Science",
    icon: FlaskConical,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/evolution_capacite_cranienne_data_analyse",
    features: [
      "Nettoyage données paléo",
      "Analyse statistique descriptive",
      "Visualisations avancées",
      "Corrélations morphologiques",
      "Synthèse scientifique"
    ],
    codeSnippet: `# Analyse évolution capacité crânienne
import seaborn as sns

# Corrélation variables morphologiques
correlation_matrix = df[[
    'cranial_capacity', 'body_mass',
    'brain_size', 'age_million_years'
]].corr()

plt.figure(figsize=(10, 8))
sns.heatmap(correlation_matrix, annot=True, 
            cmap='coolwarm', center=0)
plt.title('Matrice de corrélation')`
  },
  {
    id: 21,
    title: "NumPy Data Lab",
    subtitle: "Exercices manipulation tableaux",
    description: "Série d'exercices pratiques pour maîtriser NumPy : manipulation de tableaux, opérations mathématiques, broadcasting et analyse de données.",
    tech: ["Python", "NumPy", "Jupyter", "Mathematics"],
    image: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=800",
    category: "Fundamentals",
    icon: Calculator,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/numpy-data-lab",
    features: [
      "Création et manipulation arrays",
      "Opérations vectorisées",
      "Broadcasting avancé",
      "Algèbre linéaire",
      "Statistiques descriptives"
    ],
    codeSnippet: `# Exercices NumPy
import numpy as np

# Création et manipulation
arr = np.arange(0, 100).reshape(10, 10)

# Opérations statistiques
print(f"Mean: {arr.mean()}")
print(f"Std: {arr.std()}")
print(f"Max: {arr.max()}")

# Broadcasting
normalized = (arr - arr.mean()) / arr.std()`
  },
  {
    id: 22,
    title: "Pandas Data Lab",
    subtitle: "Manipulation et nettoyage données",
    description: "Exercices pratiques Pandas couvrant lecture CSV, exploration DataFrame, filtrage, nettoyage valeurs manquantes, agrégation et groupby.",
    tech: ["Python", "Pandas", "Matplotlib", "Jupyter"],
    image: "https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=800",
    category: "Fundamentals",
    icon: Table,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/pandas-data-lab",
    features: [
      "Lecture fichiers CSV",
      "Exploration DataFrame",
      "Filtrage conditionnel",
      "Gestion valeurs manquantes",
      "Agrégation et groupby"
    ],
    codeSnippet: `# Exercices Pandas
import pandas as pd

df = pd.read_csv('data.csv')

# Exploration
print(df.head())
print(df.describe())

# Nettoyage
df['column'] = df['column'].fillna(df['column'].mean())

# Agrégation
summary = df.groupby('category').agg({
    'value': ['sum', 'mean', 'count']
})`
  },
  {
    id: 23,
    title: "Python Foundations",
    subtitle: "Fondamentaux et POO",
    description: "Projet complet de remise à niveau Python couvrant la programmation orientée objet, structures de données, gestion fichiers et bonnes pratiques.",
    tech: ["Python", "OOP", "Algorithms", "Best Practices"],
    image: "https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=800",
    category: "Fundamentals",
    icon: Code,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/python-foundations",
    features: [
      "Programmation orientée objet",
      "Structures de données",
      "Gestion des fichiers",
      "Exceptions et erreurs",
      "Bonnes pratiques PEP8"
    ],
    codeSnippet: `# Fondamentaux Python - POO
class DataAnalyzer:
    def __init__(self, data):
        self.data = data
        self._cleaned = False
    
    def clean(self):
        self.data = [x for x in self.data if x is not None]
        self._cleaned = True
        return self
    
    def analyze(self):
        if not self._cleaned:
            self.clean()
        return {
            'mean': sum(self.data) / len(self.data),
            'count': len(self.data)
        }`
  },
  {
    id: 24,
    title: "Jupyter Notebook Guide",
    subtitle: "Tutoriel et bonnes pratiques",
    description: "Guide pratique pour maîtriser Jupyter Notebook : exécution de code, raccourcis clavier, gestion d'état et analyse de données avec exemples concrets.",
    tech: ["Jupyter", "Python", "Markdown", "Data Analysis"],
    image: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800",
    category: "Fundamentals",
    icon: BookOpen,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/jupyter-fundamentals-data-workflow",
    features: [
      "Installation et configuration",
      "Raccourcis clavier essentiels",
      "Cellules code et markdown",
      "Gestion de l'état",
      "Export et partage"
    ],
    codeSnippet: `# Guide Jupyter Notebook

# Cellule Markdown
# ## Titre de section
# Texte explicatif avec **gras** et *italique*

# Cellule Code
import pandas as pd

# Magic commands
%matplotlib inline
%timeit df.groupby('col').sum()

# Affichage riche
from IPython.display import display
display(df.head())`
  },
  {
    id: 25,
    title: "GreenCoop - Entrepôt Météo dbt",
    subtitle: "Data Warehouse météorologique avec architecture en couches",
    description: "Construction d'un entrepôt de données météorologique pour la coopérative GreenAndCoop. Architecture dbt en 3 couches (Staging → Intermediate → Marts) avec modélisation dimensionnelle pour l'analyse des données de plusieurs stations de mesure.",
    tech: ["dbt", "PostgreSQL", "Docker", "SQL", "Docker Compose", "dbt-utils"],
    image: "https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=800",
    category: "Data Engineering",
    icon: Leaf,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/greencoop-dbt",
    features: [
      "Architecture dbt 3 couches : Staging → Intermediate → Marts",
      "Modèles dim_weather_stations et fct_weather_observations",
      "Données : température, humidité, pression, vent, UV, précipitations",
      "Tests dbt automatisés (unicité, non-nullité, intégrité référentielle)",
      "Documentation dbt générée automatiquement"
    ],
    skills: ["dbt", "Data Modeling", "SQL", "Data Warehouse", "Docker"],
    value: "Entrepôt de données fiable et documenté pour analyses météorologiques agricoles",
    codeSnippet: `-- Modèle dbt Marts : fct_weather_observations
-- Couche analytique prête à l'emploi

SELECT 
    o.observation_id,
    s.station_id,
    s.station_name,
    s.latitude,
    s.longitude,
    o.observation_time,
    o.temperature_f,
    o.humidity_pct,
    o.pressure_in,
    o.wind_speed_mph,
    o.wind_direction,
    o.solar_wm2,
    o.uv_index,
    o.precip_rate_in,
    o.precip_accum_in
FROM {{ ref('stg_meteo_station_2_observations') }} o
JOIN {{ ref('dim_weather_stations') }} s 
    ON o.station_id = s.station_id
WHERE o.observation_time IS NOT NULL`
  },
  {
    id: 26,
    title: "InduTech - Infrastructure Cloud Hybride",
    subtitle: "Architecture AWS + Pipeline Streaming temps réel",
    description: "Conception et déploiement d'une infrastructure hybride cloud AWS pour InduTechData. Combinaison ressources on-premise (SQL Server, SAN, AD) et services cloud (S3, Redshift, EMR). Pipeline streaming Redpanda → Spark pour traitement temps réel des tickets support.",
    tech: ["AWS", "Apache Spark", "Redpanda", "Docker", "Python", "Kafka", "S3", "Redshift"],
    image: "https://images.unsplash.com/photo-1667984390538-3dea7a3fe33d?w=800",
    category: "Data Engineering",
    icon: Cloud,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/indutech-infra-cloud",
    features: [
      "Architecture hybride On-premise ↔ AWS Cloud",
      "Pipeline streaming : Redpanda → Spark Structured Streaming → S3",
      "Enrichissement automatique tickets par équipe support",
      "Connexion sécurisée VPN IPSec / AWS Direct Connect",
      "CDC SQL Server → Redshift via AWS DMS"
    ],
    skills: ["Cloud Architecture", "Streaming", "Apache Spark", "Kafka", "AWS"],
    value: "Infrastructure scalable traitant les tickets client en temps réel",
    codeSnippet: `# Spark Structured Streaming - Traitement tickets temps réel
from pyspark.sql import SparkSession
from pyspark.sql.functions import col, when, from_json
from pyspark.sql.types import StructType, StringType

spark = SparkSession.builder \\
    .appName("InduTech-TicketProcessor") \\
    .getOrCreate()

# Lecture flux Redpanda (compatible Kafka)
df_stream = spark.readStream \\
    .format("kafka") \\
    .option("kafka.bootstrap.servers", "redpanda:9092") \\
    .option("subscribe", "client_tickets") \\
    .option("startingOffsets", "latest") \\
    .load()

# Enrichissement par équipe support
enriched = df_stream.withColumn("equipe_support",
    when(col("type_demande") == "Technique", "Support Technique")
    .when(col("type_demande") == "Facturation", "Support Facturation")
    .when(col("type_demande") == "Livraison", "Support Livraison")
    .otherwise("Support Compte")
)

# Écriture vers S3 Data Lake
enriched.writeStream \\
    .format("json") \\
    .option("path", "s3://indutech-datalake/tickets/") \\
    .option("checkpointLocation", "/tmp/checkpoint") \\
    .start()`
  },
  {
    id: 27,
    title: "Kestra Pipeline - Analyse Vins",
    subtitle: "Orchestration ETL avec segmentation statistique",
    description: "Pipeline de données complet orchestré avec Kestra pour fusionner 3 sources hétérogènes (ERP, Web, Liaison), calculer le chiffre d'affaires et segmenter les vins premium via analyse z-score. Workflow YAML déclaratif avec trigger automatique mensuel.",
    tech: ["Kestra", "DuckDB", "Python", "SQL", "Pandas", "Scipy", "Docker"],
    image: "https://images.unsplash.com/photo-1666148670142-2f01b117e6e0?w=800",
    category: "Data Engineering",
    icon: Workflow,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/kestra-pipeline",
    features: [
      "Orchestration YAML déclaratif avec Kestra",
      "Pipeline 9 étapes : nettoyage → fusion → calcul CA → segmentation",
      "Fusion 3 sources hétérogènes (ERP + Web + Liaison)",
      "Segmentation statistique z-score > 2 pour vins premium",
      "Trigger automatique : 15 du mois à 9h00"
    ],
    skills: ["Orchestration", "ETL", "Statistiques", "DuckDB", "Pipeline Design"],
    value: "Identification automatisée des vins premium pour stratégie commerciale",
    codeSnippet: `# Segmentation vins premium par z-score statistique
import pandas as pd
import duckdb
from scipy import stats

# Connexion DuckDB
conn = duckdb.connect('pipeline.duckdb')

# Chargement dataset fusionné
df = conn.execute("SELECT * FROM dataset_final").fetchdf()

# Calcul z-score sur le prix
df['z_score'] = stats.zscore(df['prix'])

# Segmentation statistique
vins_premium = df[df['z_score'] > 2].copy()
vins_ordinaires = df[df['z_score'] <= 2].copy()

# Export résultats
vins_premium.to_csv('outputs/vins_premium.csv', index=False)
vins_ordinaires.to_csv('outputs/vins_ordinaires.csv', index=False)

# Métriques finales
print(f"Vins premium (z>2): {len(vins_premium)}")
print(f"Vins ordinaires: {len(vins_ordinaires)}")
print(f"CA total: {df['chiffre_affaires'].sum():,.2f} €")`
  },
  {
    id: 28,
    title: "RAG - Assistant Événements Culturels",
    subtitle: "Chatbot IA avec recherche sémantique FAISS",
    description: "Assistant conversationnel intelligent basé sur l'architecture RAG (Retrieval Augmented Generation). Indexation de 200 000+ événements OpenAgenda avec embeddings Mistral AI et recherche vectorielle FAISS pour recommandations personnalisées en < 1 seconde.",
    tech: ["Python", "Mistral AI", "FAISS", "LangChain", "Pandas", "OpenAgenda API"],
    image: "https://images.unsplash.com/photo-1684369175833-4b445ad6bfb5?w=800",
    category: "Machine Learning",
    icon: Bot,
    github: "https://github.com/ENDAYEaime/Portfolio/tree/main/rag-evenements-culturels",
    features: [
      "Architecture RAG complète (Retrieval + Generation)",
      "Embeddings Mistral AI (mistral-embed)",
      "Index vectoriel FAISS : recherche < 1 seconde",
      "200 000+ événements OpenAgenda indexés",
      "Tests qualité RAGAS (faithfulness, relevance)"
    ],
    skills: ["RAG", "LLM", "Vector Search", "Embeddings", "NLP"],
    value: "Recommandations culturelles personnalisées instantanées",
    codeSnippet: `# Pipeline RAG - Recherche sémantique + Génération
from langchain.vectorstores import FAISS
from mistralai.client import MistralClient
import os

client = MistralClient(api_key=os.environ["MISTRAL_API_KEY"])

def search_events(question: str, k: int = 5):
    # 1. Génération embedding de la question
    embedding_response = client.embeddings(
        model="mistral-embed",
        input=[question]
    )
    query_embedding = embedding_response.data[0].embedding
    
    # 2. Recherche vectorielle top-k
    results = faiss_index.similarity_search_by_vector(
        query_embedding, k=k
    )
    
    # 3. Construction du contexte
    context = "\\n".join([doc.page_content for doc in results])
    
    # 4. Génération réponse avec contexte
    response = client.chat(
        model="mistral-small",
        messages=[{
            "role": "user",
            "content": f"Contexte:\\n{context}\\n\\nQuestion: {question}"
        }]
    )
    return response.choices[0].message.content`
  },
  {
    id: 29,
    title: "Sport Data Solution",
    subtitle: "Analyse données sportives",
    description: "Solution complète d'analyse de données sportives combinant collecte, traitement et visualisation des performances. Projet de data science appliqué au domaine sportif pour l'aide à la décision.",
    tech: ["Python", "Pandas", "SQL", "Matplotlib", "Scikit-Learn"],
    image: "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?w=800",
    category: "Data Science",
    icon: Trophy,
    github: "https://github.com/ENDAYEaime/Portfolio/blob/main/sport-data-solution",
    features: [
      "Collecte et intégration données sportives",
      "Analyse statistique des performances",
      "Modèles prédictifs de résultats",
      "Tableaux de bord interactifs",
      "Recommandations basées sur les données"
    ],
    codeSnippet: `# Analyse performances sportives
import pandas as pd
from sklearn.ensemble import RandomForestClassifier

# Chargement données matchs
df = pd.read_csv('sport_data.csv')

# Feature engineering
df['form_index'] = df.groupby('team')['points'].transform(
    lambda x: x.rolling(5).mean()
)

# Modèle prédictif
model = RandomForestClassifier(n_estimators=100)
model.fit(X_train, y_train)

# Prédiction prochains matchs
predictions = model.predict_proba(X_upcoming)`
  }
];

const skills = [
  { category: "Langages & Outils", icon: Code, items: ["Python", "SQL", "HTML/CSS", "Jupyter", "Git", "Bash"], color: "#3b82f6" },
  { category: "Machine Learning & IA", icon: Brain, items: ["Scikit-Learn", "Pandas", "NumPy", "FAISS", "LangChain", "RAG", "Mistral AI"], color: "#8b5cf6" },
  { category: "Data Engineering", icon: Server, items: ["dbt", "Kestra", "Apache Spark", "Redpanda", "Kafka", "DuckDB", "Docker"], color: "#10b981" },
  { category: "Bases de données", icon: Database, items: ["PostgreSQL", "MongoDB", "SQL Server", "MySQL", "AWS S3"], color: "#06b6d4" },
  { category: "Visualisation", icon: PieChart, items: ["Matplotlib", "Seaborn", "Plotly", "Power BI", "Streamlit"], color: "#f59e0b" },
  { category: "Cloud & DevOps", icon: Cloud, items: ["AWS", "Docker", "ETL", "API REST", "CI/CD"], color: "#ef4444" }
];

const contacts = [
  { name: "GitHub", url: "https://github.com/ENDAYEaime", icon: Github },
  { name: "LinkedIn", url: "https://www.linkedin.com/in/aime-endaye-2a57b0150", icon: Linkedin },
  { name: "Email", url: "mailto:endayeaime@gmail.com", icon: Mail },
  { name: "Téléphone", url: "tel:0769107434", icon: Phone }
];

// Certifications Data
const certifications = [
  // Diplômes Principaux
  {
    id: 1,
    title: "Expert en Ingénierie et Science des Données",
    subtitle: "Master - Titre RNCP Niveau 7",
    issuer: "OpenClassrooms",
    date: "Juillet 2026",
    category: "Diplôme",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/titre-a-finalite-professionnelle_OpenClassrooms_Aime_Endaye_Mazina_20260709.pdf",
    featured: true,
    level: "Master"
  },
  {
    id: 22,
    title: "Data Analyst - Titre RNCP",
    subtitle: "Licence - Analyste de données certifié",
    issuer: "DSR School",
    date: "2025",
    category: "Diplôme",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/dn5awfym_1749819254706_9c764b0fba487e8c2bacefc76b9914cedf328a39294825392bc9cb443424cb9d.pdf",
    featured: true,
    level: "Licence"
  },
  // Nouvelles Certifications (Juillet 2026)
  {
    id: 23,
    title: "Initiez-vous au logiciel Airbyte pour intégrer vos données",
    issuer: "OpenClassrooms",
    date: "Juillet 2026",
    category: "Data Engineering",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/1668965129.pdf"
  },
  {
    id: 24,
    title: "Optimisez votre déploiement en créant des conteneurs avec Docker",
    issuer: "OpenClassrooms",
    date: "Juillet 2026",
    category: "Data Engineering",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/6108996663.pdf"
  },
  {
    id: 25,
    title: "Gérez votre environnement Python avec UV",
    issuer: "OpenClassrooms",
    date: "Juillet 2026",
    category: "Python & Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/7351756352.pdf"
  },
  // OpenClassrooms Certifications
  {
    id: 2,
    title: "Mettez en place un RAG pour un LLM",
    issuer: "OpenClassrooms",
    date: "Mai 2026",
    category: "IA & Machine Learning",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/8838917910.pdf"
  },
  {
    id: 3,
    title: "Initiez-vous au Machine Learning",
    issuer: "OpenClassrooms",
    date: "Mai 2026",
    category: "IA & Machine Learning",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/2401555670.pdf"
  },
  {
    id: 4,
    title: "Réalisez des calculs distribués avec Spark",
    issuer: "OpenClassrooms",
    date: "Mai 2026",
    category: "Big Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/3017742606.pdf"
  },
  {
    id: 5,
    title: "Analysez les flux en temps réel avec Redpanda",
    issuer: "OpenClassrooms",
    date: "Avril 2026",
    category: "Big Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/2992847522.pdf"
  },
  {
    id: 6,
    title: "Orchestrez vos workflows avec Kestra",
    issuer: "OpenClassrooms",
    date: "Mai 2026",
    category: "Data Engineering",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/8364552978.pdf"
  },
  {
    id: 7,
    title: "Initiez-vous à dbt Cloud",
    issuer: "OpenClassrooms",
    date: "Avril 2026",
    category: "Data Engineering",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/2197938936.pdf"
  },
  {
    id: 8,
    title: "Adoptez les approches modernes des bases de données",
    issuer: "OpenClassrooms",
    date: "Mai 2026",
    category: "Bases de données",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/1036929834.pdf"
  },
  {
    id: 9,
    title: "Implémentez vos bases de données relationnelles avec SQL",
    issuer: "OpenClassrooms",
    date: "Mai 2026",
    category: "Bases de données",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/1658521824.pdf"
  },
  {
    id: 10,
    title: "Perfectionnez votre maîtrise de SQL",
    issuer: "OpenClassrooms",
    date: "Avril 2026",
    category: "Bases de données",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/6690357913.pdf"
  },
  {
    id: 11,
    title: "Modélisez vos bases de données",
    issuer: "OpenClassrooms",
    date: "Octobre 2025",
    category: "Bases de données",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/6572912633.pdf"
  },
  {
    id: 12,
    title: "Requêtez une base de données avec SQL",
    issuer: "OpenClassrooms",
    date: "Septembre 2025",
    category: "Bases de données",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/2339933636.pdf"
  },
  {
    id: 13,
    title: "Découvrez les librairies Python pour Data Science",
    issuer: "OpenClassrooms",
    date: "Octobre 2025",
    category: "Python & Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/3111292635.pdf"
  },
  {
    id: 14,
    title: "Initiez-vous à Python pour l'analyse de données",
    issuer: "OpenClassrooms",
    date: "Octobre 2025",
    category: "Python & Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/1496080843.pdf"
  },
  {
    id: 15,
    title: "Nettoyez et analysez votre jeu de données",
    issuer: "OpenClassrooms",
    date: "Octobre 2025",
    category: "Python & Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/5659065440.pdf"
  },
  {
    id: 16,
    title: "Perfectionnez-vous sur Excel",
    issuer: "OpenClassrooms",
    date: "Août 2025",
    category: "Outils",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/7712098307.pdf"
  },
  {
    id: 17,
    title: "Maîtrisez les fondamentaux d'Excel",
    issuer: "OpenClassrooms",
    date: "Juillet 2025",
    category: "Outils",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/2489080660.pdf"
  },
  // Udemy Certifications
  {
    id: 18,
    title: "Formation Complète Python 2023 - de 0 à Expert",
    issuer: "Udemy",
    date: "Septembre 2023",
    category: "Python & Data",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/Python.pdf"
  },
  {
    id: 19,
    title: "SQL pour la Data Science de A à Z",
    issuer: "Udemy",
    date: "Novembre 2023",
    category: "Bases de données",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/SQL.pdf"
  },
  {
    id: 20,
    title: "HTML5 et CSS3 : la formation ULTIME",
    issuer: "Udemy",
    date: "Mars 2023",
    category: "Développement Web",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/HTML_CSS.pdf"
  },
  {
    id: 21,
    title: "Concevoir un scénario pédagogique",
    issuer: "Udemy",
    date: "Février 2023",
    category: "Soft Skills",
    pdfUrl: "https://github.com/ENDAYEaime/Portfolio/blob/main/diplome/Pedagogie.pdf"
  }
];

// Navbar Component
function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "À propos", href: "#about" },
    { name: "Compétences", href: "#skills" },
    { name: "Certifications", href: "#certifications" },
    { name: "Projets", href: "#projects" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#020817]/90 backdrop-blur-xl border-b border-blue-500/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#hero" data-testid="nav-logo" className="font-heading font-bold text-xl md:text-2xl gradient-text flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <Database className="w-4 h-4 text-white" />
            </div>
            EA
          </a>
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="px-4 py-2 font-medium text-slate-400 hover:text-white transition-all rounded-lg hover:bg-white/5 relative group">
                {link.name}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 transition-all group-hover:w-3/4 rounded-full" />
              </a>
            ))}
          </div>
          <button data-testid="mobile-menu-btn" onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/5">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-[#020817]/95 backdrop-blur-xl border-t border-blue-500/10 py-4">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} onClick={() => setIsOpen(false)} className="block py-3 px-4 font-medium text-slate-400 hover:text-white hover:bg-blue-500/10 rounded-lg mx-2">
                {link.name}
              </a>
            ))}
          </motion.div>
        )}
      </div>
    </nav>
  );
}

// Hero Section
function Hero() {
  return (
    <section id="hero" data-testid="hero-section" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Deep blue background */}
      <div className="absolute inset-0 bg-[#020817]" />
      
      {/* Animated grid */}
      <div className="absolute inset-0 data-grid-bg" />
      
      {/* Noise overlay */}
      <div className="absolute inset-0 noise-overlay" />
      
      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-indigo-600/15 rounded-full blur-[100px] animate-pulse" style={{animationDelay: '1s'}} />
      <div className="absolute top-1/2 right-1/3 w-[300px] h-[300px] bg-cyan-500/10 rounded-full blur-[80px] animate-pulse" style={{animationDelay: '2s'}} />

      {/* Scanning line effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent scan-line" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 relative z-10">
        <motion.div className="text-center" initial="initial" animate="animate" variants={stagger}>
          
          {/* Status badge */}
          <motion.div 
            variants={fadeUp} 
            className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-400/30 rounded-full px-6 py-2.5 mb-8 backdrop-blur-sm"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400"></span>
            </span>
            <span className="text-sm text-cyan-300 font-mono tracking-wide">Disponible pour opportunités</span>
          </motion.div>

          {/* Title */}
          <motion.div variants={fadeUp} className="mb-4">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-mono tracking-[0.2em] uppercase bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-400 border border-cyan-500/20">
              Data Science & Intelligence Artificielle
            </span>
          </motion.div>
          
          {/* Name */}
          <motion.h1 variants={fadeUp} className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-8">
            <span className="text-white">ENDAYE</span>{" "}
            <span className="gradient-text-alt glow-text">Aimé</span>
          </motion.h1>
          
          {/* Description */}
          <motion.p variants={fadeUp} className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            Transformer les <span className="text-cyan-400 font-medium">données brutes</span> en insights actionnables grâce à l'analyse avancée et le <span className="text-blue-400 font-medium">machine learning</span>.
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#projects" 
              data-testid="cta-projects" 
              className="group relative inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-medium overflow-hidden transition-all hover:-translate-y-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-blue-600/25"
            >
              <Database className="w-5 h-5 text-white" />
              <span className="text-white">Voir mes 29 projets</span>
              <ChevronDown size={18} className="text-white group-hover:translate-y-1 transition-transform" />
            </a>
            <a 
              href="#contact" 
              data-testid="cta-contact" 
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-medium border border-blue-500/40 text-blue-400 hover:border-cyan-400/60 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all backdrop-blur-sm"
            >
              <Terminal className="w-5 h-5" />
              Me contacter
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div variants={fadeUp} className="grid grid-cols-3 gap-6 mt-20 max-w-2xl mx-auto">
            {[
              { value: "29", label: "Projets Data", icon: Database, color: "from-cyan-400 to-blue-500" },
              { value: "15+", label: "Technologies", icon: Code, color: "from-blue-400 to-indigo-500" },
              { value: "2+", label: "Années XP", icon: TrendingUp, color: "from-indigo-400 to-purple-500" }
            ].map((stat, i) => (
              <motion.div 
                key={i} 
                className="relative group p-6 rounded-2xl bg-gradient-to-b from-white/5 to-transparent border border-white/5 hover:border-blue-500/30 transition-all cursor-default"
                whileHover={{ y: -5 }}
              >
                <div className={`w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br ${stat.color} p-0.5`}>
                  <div className="w-full h-full rounded-xl bg-[#020817] flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="font-heading text-4xl md:text-5xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        animate={{ y: [0, 10, 0] }} 
        transition={{ duration: 2, repeat: Infinity }}
      >
        <span className="text-xs text-slate-600 font-mono">scroll</span>
        <ChevronDown className="w-5 h-5 text-blue-500/40" />
      </motion.div>
    </section>
  );
}

// About Section
function About() {
  const formations = [
    { name: "Master Data Engineering", school: "OpenClassrooms", period: "09/2025 - 07/2026", current: false },
    { name: "Licence Data Analyst", school: "DSR School", period: "03/2025 - 06/2025", current: false },
    { name: "Formation Data Analyst", school: "Dataquest", period: "01/2022 - 07/2022", current: false },
    { name: "Biologie, Niveau Licence 3", school: "UFR de Reims", period: "09/2019 - 06/2022", current: false }
  ];

  const competencesProjet = [
    "Modélisation de bases de données (schémas en étoile, logique décisionnelle)",
    "SQL avancé et optimisation de requêtes",
    "Python pour le traitement de données (Pandas, Polars)",
    "Mise en place de pipelines ETL",
    "Réplication et sharding avec MongoDB",
    "Conteneurisation avec Docker",
    "Intégration de données via Airbyte",
    "Machine Learning (régression, feature engineering, tuning)",
    "Applications analytiques avec Streamlit"
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-[#030d1c] relative">
      <div className="absolute inset-0 data-grid-bg opacity-50" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px bg-blue-500" />
            <p className="text-blue-400 font-mono text-sm tracking-widest uppercase">À propos</p>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-12">
            Mon parcours <span className="gradient-text">Data</span>
          </motion.h2>
          
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Main Bio - Takes 3 columns */}
            <motion.div variants={fadeUp} className="lg:col-span-3 space-y-6">
              <p className="text-lg text-slate-300 leading-relaxed">
                Je m'appelle <strong className="text-blue-400">Endaye Aimé</strong>, passionné par la data et par la manière dont elle permet de structurer, analyser et optimiser des systèmes complexes.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                Mon parcours a commencé dans les <strong className="text-blue-400">sciences du vivant</strong>, où j'ai développé une approche rigoureuse, analytique et méthodologique de la résolution de problèmes. Progressivement, j'ai orienté cette rigueur scientifique vers la donnée, en débutant par la data analyse à travers plusieurs projets concrets : exploration, nettoyage, visualisation, analyses statistiques et problématiques décisionnelles.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                Avec le temps, mon intérêt s'est déplacé vers les fondations techniques des systèmes data. Je me spécialise aujourd'hui en <strong className="text-blue-400">Data Engineering</strong>, avec une approche orientée architecture, performance et scalabilité.
              </p>
              <p className="text-lg text-slate-300 leading-relaxed">
                Aujourd'hui, mon objectif est d'évoluer vers des rôles d'<strong className="text-blue-400">ingénieur data</strong> et <strong className="text-blue-400">architecte IA</strong>, en concevant des architectures robustes capables d'alimenter des systèmes d'intelligence artificielle à grande échelle.
              </p>
              
              {/* Competences from projects */}
              <div className="mt-8 p-6 bg-[#020817] border border-blue-500/20 rounded-xl">
                <h4 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-blue-400" />
                  Ce sur quoi j'ai travaillé
                </h4>
                <div className="grid sm:grid-cols-2 gap-2">
                  {competencesProjet.map((comp, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-slate-400">
                      <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <span>{comp}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Philosophy */}
              <div className="p-6 bg-gradient-to-r from-blue-600/10 to-transparent border-l-4 border-blue-500 rounded-r-xl">
                <p className="text-blue-300 italic">
                  "Chaque projet que je réalise s'inscrit dans une logique claire : concevoir des solutions data complètes, performantes et prêtes pour la production."
                </p>
              </div>
            </motion.div>

            {/* Formation - Takes 2 columns */}
            <motion.div variants={fadeUp} className="lg:col-span-2">
              <div className="bg-[#020817] border border-blue-500/30 rounded-xl p-6 sticky top-24">
                <h3 className="font-heading text-xl font-semibold text-white mb-6 flex items-center gap-3">
                  <GraduationCap className="w-5 h-5 text-blue-400" />
                  Formation
                </h3>
                <ul className="space-y-4">
                  {formations.map((item, i) => (
                    <li key={i} className="flex items-start gap-4 pb-4 border-b border-blue-500/10 last:border-0 last:pb-0">
                      <div className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${item.current ? 'bg-green-400 pulse-dot' : 'bg-blue-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white text-sm">{item.name}</div>
                        <div className="text-sm text-blue-400">{item.school}</div>
                        <div className="text-xs text-slate-500 font-mono mt-1">{item.period}</div>
                      </div>
                      {item.current && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full whitespace-nowrap">En cours</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Skills Section
function Skills() {
  return (
    <section id="skills" data-testid="skills-section" className="py-24 md:py-32 bg-[#020817] relative overflow-hidden">
      <div className="absolute inset-0 data-grid-bg" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-gradient-to-b from-blue-600/10 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px bg-blue-500" />
            <p className="text-blue-400 font-mono text-sm tracking-widest uppercase">Expertise</p>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-12">
            Stack <span className="gradient-text">technique</span>
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skill, index) => {
              const Icon = skill.icon;
              return (
                <motion.div key={skill.category} variants={fadeUp} whileHover={{ y: -8 }} data-testid={`skill-card-${index}`} className="group bg-[#0d1b2a] border border-blue-500/20 hover:border-blue-500/50 p-6 rounded-xl transition-all duration-300">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${skill.color}20`, border: `1px solid ${skill.color}40` }}>
                      <Icon className="w-5 h-5" style={{ color: skill.color }} />
                    </div>
                    <h3 className="font-heading font-semibold text-white group-hover:text-blue-400 transition-colors">{skill.category}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {skill.items.map((item) => (
                      <span key={item} className="font-mono text-xs bg-[#020817] text-slate-400 px-3 py-1.5 rounded-lg border border-blue-500/20 hover:border-blue-500/40 hover:text-blue-400 transition-colors">{item}</span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Certifications Section
function Certifications() {
  const [filter, setFilter] = React.useState("Tous");
  const categories = ["Tous", "Diplôme", "IA & Machine Learning", "Big Data", "Data Engineering", "Bases de données", "Python & Data", "Outils", "Développement Web", "Soft Skills"];
  
  const filteredCerts = filter === "Tous" ? certifications : certifications.filter(c => c.category === filter);
  const featuredDiplomas = certifications.filter(c => c.featured);

  return (
    <section id="certifications" data-testid="certifications-section" className="py-24 md:py-32 bg-[#020817] relative">
      <div className="absolute inset-0 data-grid-bg opacity-30" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px bg-blue-500" />
            <p className="text-blue-400 font-mono text-sm tracking-widest uppercase">Qualifications</p>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
            Diplômes & <span className="gradient-text">Certifications</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 mb-8">
            {certifications.length} certifications obtenues auprès de plateformes reconnues
          </motion.p>

          {/* Featured Diplomas - Master & Licence */}
          {featuredDiplomas.length > 0 && (
            <motion.div variants={fadeUp} className="mb-10 grid md:grid-cols-2 gap-4">
              {featuredDiplomas.map((diploma) => (
                <a 
                  key={diploma.id}
                  href={diploma.pdfUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="group block bg-gradient-to-r from-blue-600/20 via-cyan-600/10 to-blue-600/20 border border-blue-500/40 hover:border-cyan-400/60 rounded-2xl p-6 transition-all hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      diploma.level === "Master" 
                        ? "bg-gradient-to-br from-cyan-500 to-blue-600" 
                        : "bg-gradient-to-br from-purple-500 to-pink-600"
                    }`}>
                      <GraduationCap className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`px-3 py-1 text-xs font-mono rounded-full border ${
                          diploma.level === "Master"
                            ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                            : "bg-purple-500/20 text-purple-400 border-purple-500/30"
                        }`}>
                          {diploma.level?.toUpperCase() || "DIPLÔME"}
                        </span>
                      </div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-white group-hover:text-cyan-400 transition-colors mb-1">
                        {diploma.title}
                      </h3>
                      <p className="text-slate-400 text-sm mb-2">{diploma.subtitle}</p>
                      <div className="flex items-center gap-2 text-blue-400">
                        <Award className="w-4 h-4" />
                        <span className="text-sm font-medium">{diploma.issuer}</span>
                        <ExternalLink className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </motion.div>
          )}

          {/* Filter */}
          <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <button 
                key={cat} 
                onClick={() => setFilter(cat)} 
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  filter === cat 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                    : 'bg-[#0d1b2a] text-slate-400 border border-blue-500/20 hover:border-blue-500/50 hover:text-blue-400'
                }`}
              >
                {cat} {cat === "Tous" && `(${certifications.length})`}
              </button>
            ))}
          </motion.div>

          {/* Certifications Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCerts.filter(c => !c.featured).map((cert, index) => (
              <motion.a
                key={cert.id}
                href={cert.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                className="group bg-[#0d1b2a] border border-blue-500/20 hover:border-blue-500/50 rounded-xl p-4 transition-all cursor-pointer"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono text-slate-500">{cert.date}</span>
                    <h4 className="font-medium text-white text-sm group-hover:text-blue-400 transition-colors line-clamp-2 leading-tight mt-0.5">
                      {cert.title}
                    </h4>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${cert.issuer === 'OpenClassrooms' ? 'bg-purple-500' : 'bg-orange-500'}`} />
                    <span className="text-xs text-slate-500">{cert.issuer}</span>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="mt-3 pt-3 border-t border-blue-500/10">
                  <span className="text-xs font-mono text-blue-400/60">{cert.category}</span>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// Project Detail Modal
function ProjectModal({ project, onClose }) {
  React.useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleEscape); document.body.style.overflow = 'unset'; };
  }, [onClose]);

  if (!project) return null;
  const Icon = project.icon;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="bg-[#0d1b2a] border border-blue-500/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="relative h-48 md:h-56 overflow-hidden rounded-t-2xl">
          <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2a] via-transparent to-transparent" />
          <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"><X size={20} /></button>
          <div className="absolute bottom-4 left-6 flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center"><Icon className="w-6 h-6 text-white" /></div>
            <div>
              <span className="text-blue-400 font-mono text-xs uppercase">{project.category}</span>
              <h2 className="font-heading text-2xl font-bold text-white">{project.title}</h2>
            </div>
          </div>
        </div>
        <div className="p-6 md:p-8 space-y-6">
          <div>
            <h3 className="font-heading text-lg font-semibold text-white mb-3">Description</h3>
            <p className="text-slate-300 leading-relaxed">{project.description}</p>
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-white mb-3">Technologies utilisées</h3>
            <div className="flex flex-wrap gap-2">
              {project.tech.map((t) => (<span key={t} className="px-4 py-2 bg-blue-600/20 border border-blue-500/40 rounded-lg font-mono text-sm text-blue-400">{t}</span>))}
            </div>
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-white mb-3">Fonctionnalités & Points techniques</h3>
            <ul className="space-y-2">
              {project.features.map((feature, i) => (<li key={i} className="flex items-start gap-3 text-slate-300"><ChevronRight className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />{feature}</li>))}
            </ul>
          </div>
          
          {/* Compétences démontrées */}
          {project.skills && project.skills.length > 0 && (
            <div>
              <h3 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-cyan-400" />Compétences démontrées
              </h3>
              <div className="flex flex-wrap gap-2">
                {project.skills.map((skill, i) => (
                  <span key={i} className="px-3 py-1.5 bg-cyan-600/20 border border-cyan-500/40 rounded-full text-sm text-cyan-400">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Valeur ajoutée */}
          {project.value && (
            <div className="bg-gradient-to-r from-green-600/10 to-emerald-600/10 border border-green-500/30 rounded-xl p-4">
              <h3 className="font-heading text-lg font-semibold text-white mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />Valeur ajoutée
              </h3>
              <p className="text-green-300">{project.value}</p>
            </div>
          )}
          
          <div>
            <h3 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2"><FileCode className="w-5 h-5 text-blue-400" />Extrait de code</h3>
            <div className="code-block p-4 overflow-x-auto"><pre className="text-slate-300 text-sm whitespace-pre-wrap">{project.codeSnippet}</pre></div>
          </div>
          <div className="pt-4 border-t border-blue-500/20 flex flex-wrap gap-3">
            {project.demo && (
              <a href={project.demo} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-6 py-3 rounded-lg font-medium transition-all hover:-translate-y-1">
                <Globe className="w-5 h-5" />Voir la Démo<ExternalLink className="w-4 h-4" />
              </a>
            )}
            <a href={project.github} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-lg font-medium transition-all hover:-translate-y-1">
              <Github className="w-5 h-5" />Voir sur GitHub<ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Projects Section
function Projects() {
  const [filter, setFilter] = React.useState("Tous");
  const [selectedProject, setSelectedProject] = React.useState(null);
  const categories = ["Tous", "Machine Learning", "Data Engineering", "Business Analytics", "Data Analysis", "Data Visualization", "Data Science", "Fundamentals"];

  const filteredProjects = filter === "Tous" ? projects : projects.filter(p => p.category === filter);

  return (
    <section id="projects" data-testid="projects-section" className="py-24 md:py-32 bg-[#030d1c] relative">
      <div className="absolute inset-0 data-grid-bg opacity-30" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-px bg-blue-500" />
            <p className="text-blue-400 font-mono text-sm tracking-widest uppercase">Portfolio</p>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-4">
            Mes <span className="gradient-text">29 Projets</span>
          </h2>
          <p className="text-slate-400 mb-8">Cliquez sur un projet pour voir les détails, le code et le lien GitHub</p>
          
          {/* Filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setFilter(cat)} data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${filter === cat ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' : 'bg-[#020817] text-slate-400 border border-blue-500/20 hover:border-blue-500/50 hover:text-blue-400'}`}>
                {cat} {cat === "Tous" && `(${projects.length})`}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredProjects.map((project, index) => {
              const Icon = project.icon;
              return (
                <motion.article 
                  key={project.id} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  whileHover={{ y: -6 }} 
                  data-testid={`project-card-${index}`} 
                  className="group bg-[#020817] border border-blue-500/20 hover:border-blue-500/50 rounded-xl overflow-hidden transition-all duration-300 cursor-pointer" 
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="aspect-video overflow-hidden bg-[#0d1b2a] relative">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-80 group-hover:opacity-100" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
                    <div className="absolute top-3 left-3">
                      <div className="w-9 h-9 bg-blue-600/90 backdrop-blur rounded-lg flex items-center justify-center"><Icon className="w-4 h-4 text-white" /></div>
                    </div>
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-9 h-9 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center"><Eye className="w-4 h-4 text-white" /></div>
                    </div>
                  </div>
                  <div className="p-4">
                    <span className="font-mono text-xs text-blue-400 uppercase tracking-wider">{project.category}</span>
                    <h3 className="font-heading text-base font-semibold text-white mt-1 mb-1 group-hover:text-blue-400 transition-colors line-clamp-1">{project.title}</h3>
                    <p className="text-xs text-slate-500 mb-3 line-clamp-1">{project.subtitle}</p>
                    <div className="flex flex-wrap gap-1">
                      {project.tech.slice(0, 3).map((t) => (<span key={t} className="font-mono text-xs bg-[#0d1b2a] text-slate-500 px-2 py-0.5 rounded">{t}</span>))}
                      {project.tech.length > 3 && <span className="font-mono text-xs text-slate-600">+{project.tech.length - 3}</span>}
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </div>
      <AnimatePresence>{selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />}</AnimatePresence>
    </section>
  );
}

// Contact Section
function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#020817] relative overflow-hidden">
      <div className="absolute inset-0 data-grid-bg" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-96 bg-gradient-to-t from-blue-600/10 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger} className="text-center">
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-px bg-blue-500" />
            <p className="text-blue-400 font-mono text-sm tracking-widest uppercase">Contact</p>
            <div className="w-12 h-px bg-blue-500" />
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
            Travaillons <span className="gradient-text">ensemble</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-slate-400 max-w-xl mx-auto mb-12">
            Vous avez un projet data ? Une opportunité professionnelle ? N'hésitez pas à me contacter.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
            {contacts.map((contact) => {
              const Icon = contact.icon;
              return (
                <a key={contact.name} href={contact.url} target={contact.url.startsWith('http') ? '_blank' : undefined} rel={contact.url.startsWith('http') ? 'noopener noreferrer' : undefined} data-testid={`contact-${contact.name.toLowerCase()}`} className="group inline-flex items-center gap-3 bg-[#0d1b2a] border border-blue-500/30 hover:border-blue-500/60 px-6 py-4 rounded-xl font-medium text-slate-300 hover:text-blue-400 transition-all hover:-translate-y-1">
                  <Icon size={20} className="group-hover:scale-110 transition-transform" />
                  {contact.name}
                  {contact.url.startsWith('http') && <ExternalLink size={14} className="text-slate-500" />}
                </a>
              );
            })}
          </motion.div>
          <motion.div variants={fadeUp} className="mt-16 p-8 bg-[#0d1b2a] border border-blue-500/30 rounded-xl max-w-md mx-auto">
            <Mail className="w-8 h-8 text-blue-500 mx-auto mb-4" />
            <p className="text-slate-500 mb-2">Email direct</p>
            <a href="mailto:endayeaime@gmail.com" className="font-heading text-xl text-blue-400 hover:text-blue-300 transition-colors">endayeaime@gmail.com</a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Footer
function Footer() {
  return (
    <footer data-testid="footer" className="bg-[#010410] border-t border-blue-500/10 py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-500">
            <Database className="w-5 h-5 text-blue-500" />
            <span>© {new Date().getFullYear()} ENDAYE Aimé</span>
          </div>
          <p className="text-slate-600 text-sm">Portfolio Data Science • 29 Projets • React & Tailwind</p>
        </div>
      </div>
    </footer>
  );
}

// Main App
function App() {
  return (
    <div className="min-h-screen bg-[#020817]">
      <Navbar />
      <main>
        <Hero />
        <About />
        <Skills />
        <Certifications />
        <Projects />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
